import { Fragment } from "react";
import { useNavigate } from "react-router-dom";

import { Icon } from "@/design-system";

/**
 * Page breadcrumb, matching the reference's trail. All 10 of the reference's breadcrumbs
 * are the same shape, so this follows them exactly:
 *
 *   <div class="flex items-center gap-[var(--space-2)] text-sm text-ink-3 mb-[var(--space-2)] flex-wrap">
 *     <span class="cursor-pointer text-ink-link">Home</span>
 *     <span class="msr text-[16px]">chevron_right</span>
 *     …
 *     <span class="text-ink-2">{last}</span>
 *
 * Three details that are easy to get wrong, all of them checked against the reference:
 *
 * - **The segments are flat siblings.** Wrapping each `{separator, label}` pair in its own
 *   flex row makes the gap before a separator differ from the gap after it, because the
 *   outer and inner `gap` both apply. A `<Fragment>` per segment keeps one uniform gap.
 * - **No icon before "Home"** — the reference's first crumb is plain text. `home` appears
 *   nowhere in any of its breadcrumbs.
 * - **No `font-medium` on the current segment** — it is `text-ink-2` and nothing else.
 *
 * Two deliberate divergences: the clickable crumbs are real `<button>`s rather than the
 * reference's `cursor-pointer` spans (it is a static prototype; a span is not focusable),
 * and every non-last segment is a link because `path` is a list of strings — the reference
 * has one non-clickable middle crumb (the PDP's category) that this API can't express.
 *
 * `mb-[var(--space-2)]` is the reference's spacing and applies to all 19 call sites; none
 * of them passes a `className`, so there is nothing to conflict with. Don't add a second
 * `mb-*` through `className` — `cx` here is a plain join, not `tailwind-merge`, so which
 * one wins would depend on stylesheet order rather than on what you wrote.
 */
export default function Breadcrumb({ path, className = "" }) {
  const navigate = useNavigate();

  const getUrl = (segment) => {
    if (segment.toLowerCase() === "home") {
      return "/";
    }
    return "/" + segment.toLowerCase().replace(/\s+/g, "-");
  };

  return (
    <nav
      className={`mb-[var(--space-2)] flex flex-wrap items-center gap-[var(--space-2)] font-sans text-sm text-ink-3 ${className}`}
      aria-label="Breadcrumb"
    >
      {path.map((segment, idx) => (
        <Fragment key={`${segment}-${idx}`}>
          {idx > 0 && <Icon name="chevron_right" size={16} weight={400} />}

          {idx === path.length - 1 ? (
            <span className="text-ink-2">{segment}</span>
          ) : (
            <button
              type="button"
              onClick={() => navigate(getUrl(segment))}
              className="cursor-pointer border-0 bg-transparent p-0 font-sans text-sm text-ink-link [transition:color_var(--duration-fast)] hover:text-blue-800 focus-visible:rounded-sm focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
              aria-label={`Navigate to ${segment}`}
            >
              {segment}
            </button>
          )}
        </Fragment>
      ))}
    </nav>
  );
}
